import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import React from 'react';

import { ColorProps, SizeProps } from '../../@types';
import {
  OtherLocationNeed,
  PickTask,
  PickTaskStatus,
  ReplenishmentSummary,
} from '../../@types/picktask.type';
import { Chip } from '../../components/atoms/Chip';

export const pickTaskStatusColor = (status?: string): ColorProps => {
  switch (status) {
    case PickTaskStatus.Closed:
      return ColorProps.Success;
    case PickTaskStatus.Cancelled:
      return ColorProps.Error;
    case PickTaskStatus.InProgress:
      return ColorProps.Warning;
    default:
      return ColorProps.Default;
  }
};

export const PickTaskStatusChip: React.FC<{ status?: string }> = ({ status }) => (
  <Chip label={status ?? '-'} color={pickTaskStatusColor(status)} size={SizeProps.Medium} />
);

export const PICK_TASK_STATUS_OPTIONS = Object.values(PickTaskStatus).map((value) => ({
  value,
  label: value,
}));

// Render a timestamp in the user's local time as YYYY-MM-DD HH:mm:ss.
// Backend timestamps without an explicit zone are treated as UTC.
export const formatTs = (ts?: string | null): string => {
  if (!ts) return '-';
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(ts);
  const d = new Date(hasZone ? ts : `${ts}Z`);
  if (isNaN(d.getTime())) return ts;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Parse a YYYY-MM-DD value to a local midnight Date, rejecting impossible calendar
// dates. A plain regex + new Date(y, m-1, d) would roll invalid days forward (e.g.
// 2026-02-31 becomes 3 March), so we round-trip the parts and bail if they don't
// match. Returns the matched parts alongside the Date so callers can reuse either.
const parseYmd = (
  value: string
): { year: string; month: string; day: string; date: Date } | null => {
  // Anchored so junk around a valid date (e.g. "x2026-01-01") is rejected, but a
  // trailing time (ISO "T..." or a space-separated clock) is still accepted so
  // formatSendBy can pull the date out of a full timestamp.
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(value);
  if (!m) return null;
  const [, year, month, day] = m;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null;
  }
  return { year, month, day, date };
};

// Date-only display (YYYY-MM-DD) for the send-by due date, which is captured and
// stored as a date without a meaningful time component. Falls back to the raw
// value when it isn't a valid YYYY-MM-DD date.
export const formatSendBy = (sendBy?: string | null): string => {
  if (!sendBy) return '-';
  const parsed = parseYmd(sendBy);
  return parsed ? `${parsed.year}-${parsed.month}-${parsed.day}` : sendBy;
};

// Total parts quantity to pick for a task = sum of its history lines' qtyRequired.
export const partsRequired = (task: Pick<PickTask, 'history'>): number =>
  (task.history ?? []).reduce((sum, h) => sum + (h.qtyRequired ?? 0), 0);

export interface StockShortage {
  // Locations that still need topping up across the part type.
  locationCount: number;
  // Sum of every location's Recommended top-up.
  totalRecommendedTopUp: number;
  // Good stock available at this task's source location.
  sourceAvailable: number;
  // How far the source falls short of covering every location.
  shortfall: number;
  // Up to 5 other locations under their Recommended level (Amtek depots first),
  // shown in the collapsible "other locations" column.
  otherLocations: OtherLocationNeed[];
}

// Works out the stock-shortage picture for a single pick item, or null when there
// is no shortage (the source can cover every location's Recommended top-up, or
// there is nothing to top up).
export const computeStockShortage = (
  summary: ReplenishmentSummary | undefined
): StockShortage | null => {
  if (!summary) return null;
  const { locationCount, totalRecommendedTopUp, sourceAvailable } = summary;
  if (totalRecommendedTopUp <= 0) return null;
  if (sourceAvailable >= totalRecommendedTopUp) return null;
  return {
    locationCount,
    totalRecommendedTopUp,
    sourceAvailable,
    shortfall: Math.max(0, totalRecommendedTopUp - sourceAvailable),
    otherLocations: summary.otherLocations ?? [],
  };
};

export type SendByState = 'none' | 'soon' | 'today' | 'overdue';

export interface SendByReminder {
  state: SendByState;
  label: string;
  color: ColorProps;
  days: number; // calendar days until due (0 = today, negative = overdue)
}

// A send-by due date is considered satisfied once the task is closed (all items
// picked and shipped) or cancelled, so no reminder is shown for these statuses.
const SEND_BY_CLOSED_STATUSES: string[] = [PickTaskStatus.Closed, PickTaskStatus.Cancelled];

// Calendar-day difference between the send-by date and today, both at local
// midnight. Only the date portion of the timestamp is used so a stored time (or
// timezone offset) never skews the count.
const daysUntilSendBy = (sendBy: string): number | null => {
  const parsed = parseYmd(sendBy);
  if (!parsed) return null;
  const due = parsed.date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
};

// In-app reminder state for a pick task's optional send-by due date. Returns
// 'none' (render nothing) when there is no date, the date is more than 2 days
// off, or the task is already sent/complete/cancelled.
export const getSendByState = (task: {
  sendBy?: string | null;
  status?: PickTaskStatus | string;
}): SendByReminder => {
  const none: SendByReminder = { state: 'none', label: '', color: ColorProps.Default, days: NaN };
  if (!task.sendBy || SEND_BY_CLOSED_STATUSES.includes(String(task.status))) return none;
  const days = daysUntilSendBy(task.sendBy);
  if (days === null) return none;
  if (days < 0) return { state: 'overdue', label: 'Overdue', color: ColorProps.Error, days };
  if (days === 0) return { state: 'today', label: 'Due today', color: ColorProps.Error, days };
  if (days <= 2) {
    return {
      state: 'soon',
      label: `Due in ${days} day${days === 1 ? '' : 's'}`,
      color: ColorProps.Warning,
      days,
    };
  }
  return { ...none, days };
};

// Send-by reminder as a warning-triangle icon + coloured label (amber when due
// soon, red for due today / overdue). Renders nothing when not due soon.
export const SendByWarning: React.FC<{
  task: { sendBy?: string | null; status?: PickTaskStatus | string };
}> = ({ task }) => {
  const reminder = getSendByState(task);
  if (reminder.state === 'none') return null;
  const color = reminder.color === ColorProps.Error ? 'error.main' : 'warning.main';
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color, fontWeight: 600 }}
    >
      <WarningAmberIcon sx={{ fontSize: 15 }} />
      {reminder.label}
    </Box>
  );
};
